import { ICompetenciesProps } from "../types/interfaces/ICompetenciesProps";
import { Accordion, AccordionDetails, AccordionSummary, Box, Grid } from "@mui/material";
import { ICompetency } from "../types/interfaces/ICompetency";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useState } from "react";
import Rating from "./Rating";
import PdfLink from "./PdfLink";
import DisplayTextWithWrap from "./DisplayTextWithWrap";
import { useIlpContext } from "../hooks/useIlpContext";

//This component is responsible for rendering the competencies. 
const Competencies: React.FC<ICompetenciesProps> = ({ competencies, onRatingChange }) => {
    const [expanded, setExpanded] = useState<string | false>(false);
    const { expandAll } = useIlpContext();

    //Function to handle the accordion change.
    const handleAccordionChange = (key: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? key : false);
};

//Function to handle the rating dropdown click.
const handleDropdownClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.stopPropagation();
};

//Function to handle the rating change.
const handleRatingChange = (key: string, rating: string | null) => {
    onRatingChange(key, rating);
}

//Function to handle the link click.
const handleLinkClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.stopPropagation();
};

//Function to render the sub competencies.
const renderSubCompetencies = (subCompetencies: ICompetency[]) => {
    return (
        <Box sx={{ mb: 5 }}>
            {subCompetencies.map((subCompetency) => (
                <Accordion
                    key={subCompetency.CompetencyKey}
                    expanded={expanded === subCompetency.CompetencyKey || expandAll == true}
                    onChange={handleAccordionChange(subCompetency.CompetencyKey)}
                    sx={{ m: 1 }}>
                    <AccordionSummary sx={{
                        minHeight: "0px", mb: 5, ".MuiAccordionSummary-content": {
                            margin: 0.5,
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', overflow: 'hidden', width: '100%'
                        }
                    }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                            <Grid container columns={{ xs: 4, sm: 8, md: 7, lg: 12, xl: 12 }} spacing={2}>
                                <Grid item xs={12} sm={6} md={4}>
                                    <DisplayTextWithWrap isParent={false} displayText={subCompetency.DisplayText} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Rating competency={subCompetency} isParent={false} onRatingChange={handleRatingChange} onDropdownClick={handleDropdownClick} />
                                </Grid>
                                {subCompetency.Link && (
                                    <Grid item xs={12} sm={6} md={4}>
                                        <PdfLink competency={subCompetency} onLinkClick={handleLinkClick} />
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    </AccordionSummary>
                </Accordion>
            ))}
        </Box>
    );
};

return (
    <Box sx={{ mb: 5 }}>
        {competencies.map((competency: ICompetency) => (
            <Accordion
                key={competency.CompetencyKey}
                expanded={expanded === competency.CompetencyKey || expandAll == true}
                //expanded={isAllExpanded}
                onChange={handleAccordionChange(competency.CompetencyKey)}
                sx={{ m: 1, width: "60%", minWidth: 255, backgroundColor: expanded === competency.CompetencyKey ? "#F0F0F0" : 'inherit' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ flexDirection: "row-reverse", minHeight: "0px", ".MuiAccordionSummary-content": { marginLeft: "24px", margin: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', overflow: 'hidden', width: '100%' } }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                        <Grid container columns={{ xs: 4, sm: 8, md: 7, lg: 12, xl: 12 }} spacing={2}>
                            <Grid item xs={12} sm={6} md={4}>
                                <DisplayTextWithWrap isParent={true} displayText={competency.DisplayText} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Rating competency={competency} isParent={true} onRatingChange={handleRatingChange} onDropdownClick={handleDropdownClick} />
                            </Grid>
                            {competency.Link && (
                                <Grid item xs={12} sm={6} md={4}>
                                    <PdfLink competency={competency} onLinkClick={handleLinkClick} />
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                    {competency.SubCompetencies && competency.SubCompetencies.length > 0 && renderSubCompetencies(competency.SubCompetencies)}
                </AccordionDetails>
            </Accordion>
        ))}
    </Box>
);
};

export default Competencies;